import { Component, Element, h, Host, Prop, State } from '@stencil/core'

import Helpers from '../../../utils/helpers'
import { CssClassMap } from '../../../utils/interfaces'

/**
 * ## Design System
 *
 * Para a documentação completa de design, incluindo diretrizes de uso, acessibilidade e exemplos visuais, consulte o [Design System do GovBR](https://www.gov.br/ds/components/footer?tab=designer).
 *
 * @slot default - Slot para os itens da categoria no rodapé, utilizando br-footer-item.
 */
@Component({
  tag: 'br-footer-category',
  styleUrl: 'footer-category.scss',
  shadow: true,
})
export class FooterCategory {
  /**
   * Referência ao elemento host do componente.
   * Utilize esta propriedade para acessar e manipular o elemento do DOM associado ao componente.
   */
  @Element() el: HTMLBrFooterCategoryElement

  /**
   * Identificador único do componente; gerado automaticamente quando omitido.
   */
  @Prop({ reflect: true }) readonly customId: string = Helpers.generateUniqueId()

  /**
   * Título da categoria exibido no cabeçalho da lista.
   */
  @Prop({ reflect: true }) label: string = ''

  /**
   * Indica se o layout atual corresponde ao breakpoint de desktop.
   */
  @State() isDesktop: boolean = false

  /**
   * Define o nome do grupo de accordion.
   */
  @State() accordionGroup: string = null

  /**
   * Define o media query para o componente.
   * O media query é usado para determinar se o componente deve ser exibido em um layout de desktop ou não.
   */
  private mediaQuery: MediaQueryList

  /**
   * Define um nome para o grupo de accordion.
   * O nome do grupo é definido com base no atributo `custom-id` do elemento pai.
   */
  private setAccordionGroup() {
    this.accordionGroup = `group-${this.el.parentElement.getAttribute('custom-id')}`
  }

  /**
   * Método que retorna as classes CSS do componente.
   */
  private getCssClassMap(): CssClassMap {
    return {
      'br-footer-category': true,
    }
  }

  /**
   * Método que retorna as classes baseadas no tamanho da tela.
   */
  private getRootClassMap(): CssClassMap {
    return {
      'col-2': this.isDesktop,
      col: !this.isDesktop,
    }
  }

  /**
   * Configura o media query usado para alternar entre desktop e mobile.
   */
  private setMedia() {
    this.mediaQuery = window.matchMedia('(min-width: 992px)')
    this.isDesktop = this.mediaQuery.matches
    this.mediaQuery.addEventListener('change', this.handleMediaQueryChange)
  }

  /**
   * Método do ciclo de vida do componente, que é chamado uma vez após o componente começar a ser renderizado no DOM.
   */
  componentWillLoad() {
    this.setMedia()
    this.setAccordionGroup()
  }

  /**
   * Método do ciclo de vida do componente, que é chamado quando o componente é removido do DOM.
   */
  disconnectedCallback() {
    this.mediaQuery.removeEventListener('change', this.handleMediaQueryChange)
  }

  /**
   * Trata a mudança de media query.
   * @param event O evento de mudança de media query.
   */
  private handleMediaQueryChange = (event: MediaQueryListEvent) => {
    this.isDesktop = event.matches
  }

  /**
   * Renderiza o componente.
   * @returns JSX do componente.
   */
  render() {
    return (
      <Host class={this.getRootClassMap()}>
        <br-list
          id={this.customId}
          class={this.getCssClassMap()}
          role="list"
          hide-header-divider={this.isDesktop ? true : null}
          accordion={!this.isDesktop ? this.accordionGroup : null}
        >
          {this.label &&
            this.label.trim() !== '' &&
            (this.isDesktop ? (
              <br-item>
                <div class="content text-down-01 text-bold text-uppercase">{this.label}</div>
              </br-item>
            ) : (
              <div slot="header" class="content text-down-01 text-bold text-uppercase">
                {this.label}
              </div>
            ))}
          <slot></slot>
          {!this.isDesktop && <br-divider></br-divider>}
        </br-list>
      </Host>
    )
  }
}
